import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
  faBold,
  faCheck,
  faChevronDown,
  faItalic,
  faListOl,
  faListUl,
  faMinus,
  faPalette,
  faQuoteLeft,
  faRotateLeft,
  faRotateRight,
  faStrikethrough,
  faUnderline,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRichTextContext } from "@ventumsoftware/react-toolkit";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { TEXT_COLORS, type TextColor } from "./TextColorExtension";

type Alignment = "left" | "center" | "right" | "justify";

// Label en staal per kleur uit TextColorExtension. De werkelijke tekstkleur staat in index.css
// (`.rich-text-editor .green` e.d.); houd de stalen hier gelijk aan die tinten.
const TEXT_COLOR_OPTIONS: Record<TextColor, { label: string; swatchClass: string }> = {
  green: { label: "Groen", swatchClass: "bg-green-600" },
  amber: { label: "Oranje", swatchClass: "bg-amber-600" },
  red: { label: "Rood", swatchClass: "bg-red-600" },
  brand: { label: "Merkkleur", swatchClass: "bg-brand-700" },
};

// Alleen voor weergave: tekst zonder kleur krijgt de standaard tekstkleur.
const DEFAULT_SWATCH_CLASS = "bg-black";

// Ruimte (px) tussen knop en menu, en minimale afstand van het menu tot de rand van het scherm.
const MENU_GAP = 4;
const MENU_MARGIN = 8;

/**
 * Werkbalk voor de RichTextInput uit de toolkit, afgestemd op de extensies in richTextExtensions.
 * Moet binnen een RichTextContextProvider staan.
 */
export const CustomRichTextControlBar: FC = () => {
  const { editor } = useRichTextContext();

  // TipTap v3 rendert niet meer mee met elke transactie. Zonder dit blijft de actieve stand van de
  // knoppen achter als alleen de selectie verandert (cursor verplaatsen, tekst selecteren).
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    editor?.on("transaction", rerender);
    return () => {
      editor?.off("transaction", rerender);
    };
  }, [editor]);

  const isAligned = (alignment: Alignment) => {
    if (alignment === "left") {
      // Links is de standaard en wordt niet als attribuut opgeslagen.
      return (["center", "right", "justify"] as const).every(
        (other) => !editor?.isActive({ textAlign: other }),
      );
    }
    return !!editor?.isActive({ textAlign: alignment });
  };

  const selectColor = (color: TextColor | null) => {
    if (color) {
      editor?.chain().focus().setTextColor(color).run();
    } else {
      editor?.chain().focus().unsetTextColor().run();
    }
  };

  return (
    <div
      className={"flex flex-wrap items-center gap-x-0.5 gap-y-1 py-1"}
      role={"toolbar"}
      aria-label={"Tekstopmaak"}
    >
      <ControlButton
        label={"Ongedaan maken"}
        disabled={!editor?.can().undo()}
        onClick={() => editor?.chain().focus().undo().run()}
      >
        <FontAwesomeIcon icon={faRotateLeft} />
      </ControlButton>
      <ControlButton
        label={"Opnieuw"}
        disabled={!editor?.can().redo()}
        onClick={() => editor?.chain().focus().redo().run()}
      >
        <FontAwesomeIcon icon={faRotateRight} />
      </ControlButton>

      <Divider />

      <ControlButton
        label={"Kop 1"}
        active={editor?.isActive("heading", { level: 1 })}
        onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <span className={"text-xs font-bold"}>H1</span>
      </ControlButton>
      <ControlButton
        label={"Kop 2"}
        active={editor?.isActive("heading", { level: 2 })}
        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <span className={"text-xs font-bold"}>H2</span>
      </ControlButton>
      <ControlButton
        label={"Kop 3"}
        active={editor?.isActive("heading", { level: 3 })}
        onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <span className={"text-xs font-bold"}>H3</span>
      </ControlButton>

      <Divider />

      <ControlButton
        label={"Vet"}
        active={editor?.isActive("bold")}
        onClick={() => editor?.chain().focus().toggleBold().run()}
      >
        <FontAwesomeIcon icon={faBold} />
      </ControlButton>
      <ControlButton
        label={"Cursief"}
        active={editor?.isActive("italic")}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
      >
        <FontAwesomeIcon icon={faItalic} />
      </ControlButton>
      <ControlButton
        label={"Onderstrepen"}
        active={editor?.isActive("underline")}
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
      >
        <FontAwesomeIcon icon={faUnderline} />
      </ControlButton>
      <ControlButton
        label={"Doorhalen"}
        active={editor?.isActive("strike")}
        onClick={() => editor?.chain().focus().toggleStrike().run()}
      >
        <FontAwesomeIcon icon={faStrikethrough} />
      </ControlButton>

      <Divider />

      <TextColorDropdown
        current={editor?.getAttributes("textColor").color ?? null}
        onSelect={selectColor}
      />

      <Divider />

      <ControlButton
        label={"Links uitlijnen"}
        active={isAligned("left")}
        onClick={() => editor?.chain().focus().setTextAlign("left").run()}
      >
        <FontAwesomeIcon icon={faAlignLeft} />
      </ControlButton>
      <ControlButton
        label={"Centreren"}
        active={isAligned("center")}
        onClick={() => editor?.chain().focus().toggleTextAlign("center").run()}
      >
        <FontAwesomeIcon icon={faAlignCenter} />
      </ControlButton>
      <ControlButton
        label={"Rechts uitlijnen"}
        active={isAligned("right")}
        onClick={() => editor?.chain().focus().toggleTextAlign("right").run()}
      >
        <FontAwesomeIcon icon={faAlignRight} />
      </ControlButton>
      <ControlButton
        label={"Uitvullen"}
        active={isAligned("justify")}
        onClick={() => editor?.chain().focus().toggleTextAlign("justify").run()}
      >
        <FontAwesomeIcon icon={faAlignJustify} />
      </ControlButton>

      <Divider />

      <ControlButton
        label={"Opsomming"}
        active={editor?.isActive("bulletList")}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
      >
        <FontAwesomeIcon icon={faListUl} />
      </ControlButton>
      <ControlButton
        label={"Genummerde lijst"}
        active={editor?.isActive("orderedList")}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
      >
        <FontAwesomeIcon icon={faListOl} />
      </ControlButton>

      <Divider />

      <ControlButton
        label={"Citaat"}
        active={editor?.isActive("blockquote")}
        onClick={() => editor?.chain().focus().toggleBlockquote().run()}
      >
        <FontAwesomeIcon icon={faQuoteLeft} />
      </ControlButton>
      <ControlButton
        label={"Horizontale lijn"}
        onClick={() => editor?.chain().focus().setHorizontalRule().run()}
      >
        <FontAwesomeIcon icon={faMinus} />
      </ControlButton>
    </div>
  );
};

const ControlButton: FC<{
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}> = (props) => {
  return (
    <button
      type={"button"}
      title={props.label}
      aria-label={props.label}
      aria-pressed={props.active ?? false}
      disabled={props.disabled}
      // Voorkomt dat de editor de focus (en daarmee de selectie) kwijtraakt bij een klik.
      onMouseDown={(e) => e.preventDefault()}
      onClick={props.onClick}
      className={`flex items-center justify-center size-8 shrink-0 rounded-sm border text-sm transition-colors cursor-pointer outline-brand-700 disabled:cursor-not-allowed disabled:opacity-40 ${
        props.active
          ? "bg-brand-100 text-brand-700 border-brand-300"
          : "border-transparent text-slate-600 enabled:hover:bg-slate-100 enabled:hover:text-slate-900"
      }`}
    >
      {props.children}
    </button>
  );
};

const TextColorDropdown: FC<{
  /** De kleur op de huidige selectie/cursor, of `null` als er geen kleur is ingesteld. */
  current: TextColor | null;
  /** `null` verwijdert de kleur. */
  onSelect: (color: TextColor | null) => void;
}> = (props) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Het menu staat in een portal op document.body: de editor zit in containers met overflow-hidden
  // en een scrollende modal, die een gewoon (absolute) kind-element zouden afknippen. Daardoor moet
  // de positie zelf bepaald worden, relatief tot de knop.
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const below = rect.bottom + MENU_GAP;
    const above = rect.top - MENU_GAP - menu.offsetHeight;
    const fitsBelow = below + menu.offsetHeight <= window.innerHeight - MENU_MARGIN;
    const top = fitsBelow || above < MENU_MARGIN ? below : above;
    const left = Math.max(
      MENU_MARGIN,
      Math.min(rect.left, window.innerWidth - menu.offsetWidth - MENU_MARGIN),
    );
    setPosition((old) => (old.top === top && old.left === left ? old : { top, left }));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    // Capture: scroll-events bubblen niet, en de modal heeft een eigen scrollcontainer.
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // De ModalProvider sluit de hele modal op Escape; hier moet alleen het menu dicht.
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const select = (color: TextColor | null) => {
    // Opnieuw klikken op de actieve kleur haalt hem weg.
    props.onSelect(color === props.current ? null : color);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type={"button"}
        title={"Tekstkleur"}
        aria-label={"Tekstkleur"}
        aria-haspopup={"menu"}
        aria-expanded={open}
        // Voorkomt dat de editor de focus (en daarmee de selectie) kwijtraakt bij een klik.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((old) => !old)}
        className={`flex items-center gap-1.5 h-8 px-2 rounded-sm border text-sm transition-colors cursor-pointer outline-brand-700 ${
          open
            ? "bg-slate-100 border-slate-300 text-slate-900"
            : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`}
      >
        <FontAwesomeIcon icon={faPalette} />
        <span
          className={`h-3.5 w-8 rounded-xs border border-black/10 ${
            props.current ? TEXT_COLOR_OPTIONS[props.current].swatchClass : DEFAULT_SWATCH_CLASS
          }`}
          aria-hidden={"true"}
        />
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`text-[10px] text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role={"menu"}
            aria-label={"Tekstkleur"}
            style={{ top: position.top, left: position.left }}
            // z-index boven de ModalProvider (z-50), waar de editor in staat.
            className={
              "fixed z-[60] w-52 rounded-md border border-slate-200 bg-white p-1 shadow-lg"
            }
          >
            {TEXT_COLORS.map((color) => (
              <ColorMenuItem
                key={color}
                label={TEXT_COLOR_OPTIONS[color].label}
                swatchClass={TEXT_COLOR_OPTIONS[color].swatchClass}
                checked={color === props.current}
                onClick={() => select(color)}
              />
            ))}
            <hr className={"my-1 border-slate-200"} />
            <ColorMenuItem
              label={"Kleur verwijderen"}
              swatchClass={DEFAULT_SWATCH_CLASS}
              onClick={() => select(null)}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

const ColorMenuItem: FC<{
  label: string;
  swatchClass: string;
  /** Weglaten voor een gewone actie die geen aan/uit-stand heeft. */
  checked?: boolean;
  onClick: () => void;
}> = (props) => {
  return (
    <button
      type={"button"}
      {...(props.checked === undefined
        ? { role: "menuitem" }
        : { role: "menuitemcheckbox", "aria-checked": props.checked })}
      onMouseDown={(e) => e.preventDefault()}
      onClick={props.onClick}
      className={`flex w-full items-center gap-3 rounded-sm px-2 py-1.5 text-left text-sm cursor-pointer outline-brand-700 ${
        props.checked
          ? "bg-brand-50 font-medium text-brand-700"
          : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <span
        className={`h-4 w-9 shrink-0 rounded-xs border border-black/10 ${props.swatchClass}`}
        aria-hidden={"true"}
      />
      <span className={"flex-1"}>{props.label}</span>
      {props.checked && <FontAwesomeIcon icon={faCheck} className={"text-xs"} />}
    </button>
  );
};

const Divider: FC = () => {
  return <div className={"mx-1 h-5 w-px bg-slate-300"} aria-hidden={"true"} />;
};
